import resolveSalesChannelId from './resolveSalesChannelId';

/**
 * Builds per-variant media rows for a channel.
 * When `masterVariantIds` is provided, every id gets an entry (empty `assetIds` = clear/unselect).
 */
const buildVariantAssetsForChannel = (channelId, variantMediaByChannel = {}, masterVariantIds = null) => {
  const channelVariantMedia = variantMediaByChannel[channelId] || {};
  const scopedMasterVariantIds = masterVariantIds?.length
    ? masterVariantIds.map(String)
    : null;
  const byMasterVariant = new Map();

  Object.entries(channelVariantMedia).forEach(([assetId, variantIds]) => {
    (variantIds || []).forEach((masterVariantId) => {
      const id = String(masterVariantId);
      if (scopedMasterVariantIds && !scopedMasterVariantIds.includes(id)) {
        return;
      }

      const entry = byMasterVariant.get(id) || {
        masterVariantId: id,
        assetIds: [],
      };

      if (!entry.assetIds.includes(assetId)) {
        entry.assetIds.push(assetId);
      }

      byMasterVariant.set(id, entry);
    });
  });

  if (scopedMasterVariantIds) {
    return scopedMasterVariantIds.map((id) => (
      byMasterVariant.get(id) || { masterVariantId: id, assetIds: [] }
    ));
  }

  return [...byMasterVariant.values()];
};

/**
 * Which master variants get media rows (incl. empty clears).
 * - Explicit sync scope → those ids.
 * - Product-level + any variant assignment → all product variants (omit = unselect).
 * - Pure gallery / skipped variant step → null (no variantAssets; server leaves companions alone).
 */
export const resolveMediaMasterVariantIds = ({
  masterVariantIds,
  product,
  variantMediaByChannel = {},
  variantMediaStepCompleted = false,
}) => {
  if (masterVariantIds?.length) {
    return masterVariantIds.map(String);
  }

  const hasVariantAssignments = Object.values(variantMediaByChannel)
    .some((channelMap) => Object.keys(channelMap || {}).length > 0);

  if (!variantMediaStepCompleted && !hasVariantAssignments) {
    return null;
  }

  return (product?.variants || []).map(({ _id }) => String(_id));
};

export const shouldIncludeDraftMedia = (publishingScope) => {
  const media = publishingScope?.media;
  if (media === false || media === 'none' || media == null) {
    return false;
  }
  return true;
};

export const shouldIncludeGalleryAssets = ({
  isVariantSync = false,
  copyLiveAssets = false,
  productMediaByChannel = {},
} = {}) => {
  if (isVariantSync) return false;
  if (!copyLiveAssets) return true;
  return Object.values(productMediaByChannel).some((ids) => ids?.length > 0);
};

const mapBooleanText = (text) => (text === false ? 'none' : 'fromMaster');

/**
 * Maps setup-dialog booleans to createDraft / publishDirectly / bulk enum.
 * String enums from Figma Add/Replace pass through.
 */
export const buildCanonicalPublishingScope = (publishingScope) => {
  const text = publishingScope?.text;
  const media = publishingScope?.media;

  if (typeof text === 'string' || typeof media === 'string') {
    return {
      text: typeof text === 'string' ? text : mapBooleanText(text),
      media: typeof media === 'string'
        ? media
        : (shouldIncludeDraftMedia(publishingScope) ? 'replace' : 'none'),
    };
  }

  return {
    text: mapBooleanText(text),
    media: shouldIncludeDraftMedia(publishingScope) ? 'replace' : 'none',
  };
};

/**
 * Bulk has no asset picker. Canonical enum plus mediaSource when media is in scope.
 */
export const buildBulkPublishingScope = (publishingScope) => {
  const canonical = buildCanonicalPublishingScope(publishingScope);
  if (canonical.media === 'none') {
    return canonical;
  }

  return {
    ...canonical,
    mediaSource: publishingScope?.mediaSource || 'master',
  };
};

export const shouldReplaceExistingDraftAssets = ({
  publishingScope,
  overrideExisting = false,
}) => {
  if (typeof publishingScope?.media === 'string') {
    return publishingScope.media === 'replace';
  }

  return Boolean(overrideExisting && shouldIncludeDraftMedia(publishingScope));
};

export const buildCreateDraftMediaForChannel = ({
  channelId,
  productMediaByChannel = {},
  variantMediaByChannel = {},
  masterVariantIds = null,
  includeGallery = true,
  mediaAltTextByChannel = {},
}) => {
  const galleryAssetIds = includeGallery ? (productMediaByChannel[channelId] || []) : [];
  const variantAssets = buildVariantAssetsForChannel(
    channelId,
    variantMediaByChannel,
    masterVariantIds,
  );
  const mediaAltText = mediaAltTextByChannel[channelId] || [];

  return {
    ...(includeGallery ? { galleryAssetIds } : {}),
    ...(variantAssets.length ? { variantAssets } : {}),
    ...(mediaAltText.length ? { mediaAltText } : {}),
  };
};

export const buildListingMediaPayload = ({
  channelIds,
  productMediaByChannel = {},
  variantMediaByChannel = {},
  salesChannels = [],
  masterVariantIds = null,
  includeGallery = true,
  mediaAltTextByChannel = {},
}) => channelIds.map((channelId) => ({
  salesChannelId: resolveSalesChannelId(channelId, salesChannels),
  ...buildCreateDraftMediaForChannel({
    channelId,
    productMediaByChannel,
    variantMediaByChannel,
    masterVariantIds,
    includeGallery,
    mediaAltTextByChannel,
  }),
})).filter((entry) => (
  Object.prototype.hasOwnProperty.call(entry, 'galleryAssetIds')
  || entry.variantAssets?.length
  || entry.mediaAltText?.length
));

export default buildCreateDraftMediaForChannel;
